import { Skeleton, Typography } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import CardHeader from '@mui/material/CardHeader';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
// @mui
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/navigation';
import posthog from 'posthog-js';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import TextMaxLine from 'src/components/text-max-line';
import { IEvent } from 'src/types/events';
import { fEventStartDate } from 'src/utils/format-time';

import { attendedParticipant, getDefaultPicture } from '../../utils/picture';

interface Props extends BoxProps {
  title?: string;
  subheader?: string;
  list: IEvent[];
  isLoading: boolean;
}

export default function PastEvent({ title, subheader, list, isLoading, sx, ...other }: Props) {
  const theme = useTheme();

  return (
    <Box sx={{ py: 2, ...sx }} {...other}>
      <CardHeader
        title={title ?? 'Trending'}
        sx={{
          p: 0,
          mt: 2,
          mb: 3,
        }}
      />
      {isLoading && <Skeleton sx={{ borderRadius: 1.5, height: '20rem', flexShrink: 0 }} />}
      {!isLoading && !list.length && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          width={1}
          height="15rem"
        >
          <Typography variant="caption" sx={{ color: theme.palette.grey[50] }}>
            No new events
          </Typography>
        </Stack>
      )}
      <Box
        gap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(1, 1fr)',
          md: 'repeat(2, 1fr)',
        }}
      >
        {!isLoading &&
          list.length > 0 &&
          list.map((item) => <PastEventsItem key={item.id} item={item} />)}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

type LiveEventsItemProps = {
  item: IEvent;
};

function PastEventsItem({ item }: LiveEventsItemProps) {
  const router = useRouter();

  const { title, startDate, pictures, venue, timezone, id, eventParticipantsCount } = item;

  const eventDefaultPicture = item?.cover ?? getDefaultPicture(pictures);

  const onClickEvent = () => {
    router.push(`/event/${id}`);
    posthog.capture('event_page_opened', { eventId: id });
  };

  return (
    <Paper
      onClick={onClickEvent}
      sx={{
        borderRadius: 0.5,
        bgcolor: 'transparent',
      }}
    >
      <Stack direction="row" gap={2}>
        <Box width="35%" maxWidth="35%">
          <Image
            alt={eventDefaultPicture?.url}
            src={eventDefaultPicture?.url}
            ratio="1/1"
            sx={{ borderRadius: 0.5 }}
          />
        </Box>
        <Stack
          alignItems="flex-start"
          justifyContent="space-between"
          width="60%"
          gap={2}
          sx={{
            paddingLeft: 1,
            py: 1,
            flex: 1,
            flexGrow: 1,
          }}
        >
          <Stack gap={0.5}>
            <TextMaxLine variant="h6" line={1}>
              {title}
            </TextMaxLine>
            <Typography color="#868686" variant="body1" fontSize={15}>
              {venue.name}
            </Typography>
          </Stack>
          <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="date" fontSize={15}>
              {fEventStartDate(startDate, timezone)}
            </Typography>

            {eventParticipantsCount > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  px: 1,
                  py: 0.5,
                  maxWidth: 'max-content',
                  borderRadius: 1,
                  border: '0.5px solid #fff',
                }}
              >
                <Iconify icon="tabler:users" color="#8e8e8e" height={15} width={15} />
                <Typography variant="caption">
                  {attendedParticipant(eventParticipantsCount)}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
